import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { toast } from "react-hot-toast";
import SettingsOptions, { options as settingsOptionsList } from "../../components/Settings/SettingsOption";
import SettingsDetails from "../../components/Settings/SettingsDetails";
import PanelHeading from "../../components/Navbar/PanelHeading";
import FacebookPagePickerDialog from "../../components/Settings/Features/FacebookPagePickerDialog";
import { fetchUserPermissions } from "../../redux/Slice/User/userDetailsSlice";
import { setSelectedOption } from "../../redux/Slice/Settings/settingsSlice";
import { setCurrentPage } from "../../redux/Slice/Dashboard/leftNavSlice";
import { fetchWorkspaces } from "../../redux/Slice/Workspace/workspaceSlice";

function Settings() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // 0-599px
  
  // State to control showing sidebar or content on mobile (xs screens)
  const [showSettingsContent, setShowSettingsContent] = useState(false);
  const [, setUserClickedOption] = useState(false); // Track if user explicitly clicked an option
  const [facebookSelection, setFacebookSelection] = useState(null); // pending Page picker id
  const selectedOption = useSelector((state) => state.settings.selectedOption);
  // Same icon list the sidebar uses (SettingsOption.jsx) -- single source of
  // truth so the content header's icon/description can never drift out of
  // sync with the sidebar's own icon for the same section.
  const currentSettingsOption = settingsOptionsList.find(
    (o) => o.name === (selectedOption || "General")
  );
  const CurrentSectionIcon = currentSettingsOption?.Icon;

  useEffect(() => {
    dispatch(fetchUserPermissions());
    dispatch(setCurrentPage("Settings"));
    // Reset to sidebar view when Settings page loads on xs screens
    if (isXs) {
      setShowSettingsContent(false);
      setUserClickedOption(false);
    }
  }, [dispatch, isXs]);



  // After Instagram OAuth, the server redirects here via
  // PostOAuthHomeEntry with ?instagram=connected|error — surface a toast,
  // land on the Integrations tab, then strip the params from the URL.
  useEffect(() => {
    const url = new URL(window.location.href);
    const instagram = url.searchParams.get("instagram");
    if (!instagram) return;

    const msg = url.searchParams.get("msg");
    dispatch(setSelectedOption("Integrations"));
    if (instagram === "connected") {
      toast.success("Instagram connected successfully.");
    } else if (instagram === "error") {
      toast.error(msg ? decodeURIComponent(msg) : "Instagram connection failed.");
    }

    url.searchParams.delete("instagram");
    url.searchParams.delete("msg");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [dispatch]);

  // After Facebook Page OAuth: ?facebook=connected|error like Instagram, plus
  // ?facebook=select&selection=<id> when the user manages several Pages and
  // must pick one (FacebookPagePickerDialog finishes the connection).
  useEffect(() => {
    const url = new URL(window.location.href);
    const facebook = url.searchParams.get("facebook");
    if (!facebook) return;

    const msg = url.searchParams.get("msg");
    const selection = url.searchParams.get("selection");
    dispatch(setSelectedOption("Integrations"));
    if (facebook === "connected") {
      toast.success("Facebook Page connected successfully.");
      dispatch(fetchWorkspaces());
    } else if (facebook === "select" && selection) {
      setFacebookSelection(selection);
    } else if (facebook === "error") {
      toast.error(msg ? decodeURIComponent(msg) : "Facebook connection failed.");
    }

    url.searchParams.delete("facebook");
    url.searchParams.delete("msg");
    url.searchParams.delete("selection");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [dispatch]);

  // After Shopify OAuth, the server redirects here via PostOAuthHomeEntry with
  // ?shopify=connected|error — same shape as the Instagram handler above.
  useEffect(() => {
    const url = new URL(window.location.href);
    const shopify = url.searchParams.get("shopify");
    if (!shopify) return;

    const msg = url.searchParams.get("msg");
    dispatch(setSelectedOption("Integrations"));
    if (shopify === "connected") {
      toast.success("Shopify store connected successfully.");
      // The OAuth callback redirects back via PostOAuthHomeEntry's client-side
      // <Navigate>, not a real browser reload — currentWorkspace in Redux is
      // whatever was loaded earlier in the session and never refreshes on its
      // own. Without this, shopifyConnected is already true in the DB but the
      // Integrations page keeps showing "Connect" until a hard refresh/relogin.
      // fetchWorkspaces.fulfilled already knows how to patch currentWorkspace
      // in place (see workspaceSlice.js) — same call ConnectWhatsAppDialog.jsx
      // already makes right after its own connect action succeeds.
      dispatch(fetchWorkspaces());
    } else if (shopify === "error") {
      toast.error(msg ? decodeURIComponent(msg) : "Shopify connection failed.");
    }

    url.searchParams.delete("shopify");
    url.searchParams.delete("msg");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [dispatch]);

  // Reset to sidebar view when screen size changes from xs to larger
  useEffect(() => {
    if (!isXs) {
      setShowSettingsContent(false);
      setUserClickedOption(false);
    }
  }, [isXs]);

  const handleBackToSidebar = () => {
    setShowSettingsContent(false);
    setUserClickedOption(false);
  };

  const handleOptionClick = () => {
    // Only show content when user explicitly clicks an option on xs screens
    if (isXs) {
      setShowSettingsContent(true);
      setUserClickedOption(true);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        margin: 0,
        padding: 0,
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        // Hide scrollbar but keep scrolling functionality for xs and sm
        scrollbarWidth: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" }, // Firefox
        msOverflowStyle: { xs: "none", sm: "none", md: "auto", lg: "auto", xl: "auto" }, // IE and Edge (old)
        "&::-webkit-scrollbar": {
          width: { xs: 0, sm: 0, md: "auto", lg: "auto", xl: "auto" }, // Hide scrollbar on xs and sm
        },
      }}
    >
      <Grid
        container
        sx={{
          width: "100%",
          height: "100%",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Main Content Area */}
        <Grid
          item
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: 1,
              minHeight: 0,
              width: "100%",
              overflow: "hidden",
            }}
          >
            {/* For XS screens: Show sidebar OR content, not both */}
            {isXs ? (
              <>
                {!showSettingsContent ? (
                  <Box
                    sx={{
                      width: "100%",
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      backgroundColor: "background.default",
                      borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <SettingsOptions onOptionClick={handleOptionClick} />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      backgroundColor: "background.default",
                      // Integrations owns its own 16px inset (see Integrations.jsx)
                      padding: selectedOption === "Integrations"
                        ? 0
                        : selectedOption === "Team Members"
                        ? {
                            xs: "14px 12px 12px 12px",
                            sm: "20px",
                            md: "20px",
                            lg: "20px",
                            xl: "20px",
                          }
                        : { xs: "12px", sm: "20px", md: "20px", lg: "20px", xl: "20px" },
                      boxSizing: "border-box",
                    }}
                  >
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                      }}
                    >
                      <SettingsDetails onBack={handleBackToSidebar} />
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <>
                {/* Fixed 280px, matching Inbox's rail exactly (was 20%-responsive) --
                    PanelHeading lives inside SettingsOptions itself, same as Inbox's
                    own "Inbox" heading living inside InboxSettings.jsx. */}
                <Box
                  sx={{
                    width: "280px",
                    flexShrink: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    // Matches Inbox's rail exactly -- InboxSettings.jsx sets no
                    // background of its own at all, it just inherits the page's
                    // own background.default (confirmed in Inbox.jsx). Was
                    // background.paper, a visibly different "card" surface color.
                    backgroundColor: "background.default",
                    borderRight: (theme) => `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <SettingsOptions onOptionClick={handleOptionClick} />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    backgroundColor: "background.default",
                  }}
                >
                  {/* Second, independent header -- starts exactly where the sidebar
                      ends, shows the current section (not just "Settings" again). */}
                  <PanelHeading
                    title={selectedOption || "General"}
                    subtitle={currentSettingsOption?.description}
                    icon={
                      CurrentSectionIcon && (
                        <CurrentSectionIcon size="100%" color={theme.palette.text.secondary} variant="Linear" />
                      )
                    }
                    variant="full"
                  />
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                      // Integrations owns its own 16px inset (see Integrations.jsx) so
                      // its cards' hover shadow isn't clipped by the scroll container.
                      padding: selectedOption === "Integrations" ? 0 : { sm: 2.5, md: 3, lg: 3.5, xl: 4 },
                      boxSizing: "border-box",
                    }}
                  >
                    <SettingsDetails />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>

      <FacebookPagePickerDialog
        selection={facebookSelection}
        onClose={() => setFacebookSelection(null)}
        onConnected={() => {
          setFacebookSelection(null);
          dispatch(fetchWorkspaces());
        }}
      />
    </Box>
  );
}

export default Settings;
